/**
 * Kpop Spotlight — the stage's in-place player (front page and single page).
 *
 * "Katso video" (or the play disc) turns the stage into a theater: the copy
 * fades out, the frame flies from its slot to a full-width 16:9 player while
 * the stage animates its height (so the page below slides instead of jumping),
 * the lights dim, and the copy returns as a strip under the video. A clone does
 * the flying while the real layout has already switched underneath it. Plain
 * WAAPI, so it runs the same in every browser; reduced motion gets the end
 * state straight away.
 *
 * Both links are real links to YouTube, so without JS they simply open there.
 */

import { EASE, embed, isPlainClick, px, reducedMotion, staggerIn, unembed } from './player.js';

const FLIGHT = 720;
const busy = new WeakSet();

const parts = (stage) => ({
	media: stage.querySelector('.spot-stage__media'),
	body: stage.querySelector('.spot-stage__body'),
	close: stage.querySelector('[data-spot-close]'),
});

const visibleKids = (el) => [...el.children].filter((c) => c.offsetParent !== null);

function fadeOut(els, ms) {
	return Promise.all(
		els.map(
			(el) =>
				el.animate([{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'translateY(-6px)' }], {
					duration: ms,
					easing: 'ease-in',
					fill: 'forwards',
				}).finished
		)
	);
}

function relRect(el, origin) {
	const r = el.getBoundingClientRect();
	return { left: r.left - origin.left, top: r.top - origin.top, width: r.width, height: r.height };
}

/** Bring the stage's top edge into view below the sticky header first. */
function frameStage(stage) {
	const header = document.querySelector('.wp-site-blocks > header');
	const offset = (header ? header.offsetHeight : 0) + 16;
	const top = stage.getBoundingClientRect().top;
	if (top >= offset && top < window.innerHeight * 0.35) return Promise.resolve();

	return new Promise((done) => {
		const timer = setTimeout(done, 700);
		window.addEventListener(
			'scrollend',
			() => {
				clearTimeout(timer);
				done();
			},
			{ once: true }
		);
		window.scrollTo({ top: window.scrollY + top - offset, behavior: 'smooth' });
	});
}

async function theater(stage, on) {
	const { media, body, close } = parts(stage);
	const origin = stage.getBoundingClientRect();
	const from = relRect(media, origin);
	const h0 = stage.offsetHeight;

	await fadeOut([...visibleKids(body), ...media.querySelectorAll('.spot-stage__play')], 170);

	const ghost = media.cloneNode(true);
	ghost.querySelectorAll('.spot-stage__play, iframe, .spot-eq').forEach((n) => n.remove());
	ghost.classList.add('spot-ghost');
	ghost.removeAttribute('id');
	Object.assign(ghost.style, px(from));
	stage.append(ghost);

	media.style.opacity = '0';
	body.style.opacity = '0';
	stage.classList.toggle('is-playing', on);
	close.hidden = !on;
	body.getAnimations({ subtree: true }).forEach((a) => a.cancel());
	media.querySelectorAll('.spot-stage__play').forEach((n) => n.getAnimations().forEach((a) => a.cancel()));

	const to = relRect(media, stage.getBoundingClientRect());
	const h1 = stage.offsetHeight;
	const grow = stage.animate([{ height: `${h0}px` }, { height: `${h1}px` }], { duration: FLIGHT, easing: EASE });
	const fly = ghost.animate([px(from), px(to)], { duration: FLIGHT, easing: EASE, fill: 'forwards' });
	await Promise.all([fly.finished, grow.finished]);

	media.style.opacity = '';
	ghost.remove();
	body.style.opacity = '';
	staggerIn(visibleKids(body));
	if (!on) {
		media.querySelectorAll('.spot-stage__play').forEach((n) =>
			n.animate([{ opacity: 0, transform: 'scale(.7)' }, { opacity: 1, transform: 'none' }], { duration: 420, easing: EASE })
		);
	}
}

export async function play(stage) {
	if (busy.has(stage) || stage.classList.contains('is-playing')) return;
	const { media, close } = parts(stage);
	if (!media || !close || !stage.dataset.spotVideo) return;
	busy.add(stage);
	try {
		if (reducedMotion()) {
			stage.classList.add('is-playing');
			close.hidden = false;
		} else {
			await frameStage(stage);
			await theater(stage, true);
		}
		embed(media, stage.dataset.spotVideo, stage.dataset.spotTitle);
		close.focus({ preventScroll: true });
	} finally {
		busy.delete(stage);
	}
}

export async function stop(stage) {
	if (busy.has(stage) || !stage.classList.contains('is-playing')) return;
	const { media, close } = parts(stage);
	busy.add(stage);
	try {
		unembed(media);
		if (reducedMotion()) {
			stage.classList.remove('is-playing');
			close.hidden = true;
		} else {
			await theater(stage, false);
		}
		media.querySelector('.spot-stage__play')?.focus({ preventScroll: true });
	} finally {
		busy.delete(stage);
	}
}

/** Tear the player down at once, for when the stage is about to be replaced. */
export function teardown(stage) {
	const { media } = parts(stage);
	if (media) unembed(media);
}

export const isBusy = (stage) => busy.has(stage);

export function initStages() {
	if (!document.querySelector('[data-spot-stage]')) return;

	// Delegated, so stages swapped in by the queue work without rebinding.
	document.addEventListener('click', (e) => {
		const trigger = e.target.closest('[data-spot-play], [data-spot-close]');
		const stage = trigger?.closest('[data-spot-stage]');
		if (!stage) return;
		if (trigger.matches('[data-spot-play]')) {
			if (!isPlainClick(e)) return;
			e.preventDefault();
			play(stage);
		} else {
			stop(stage);
		}
	});

	document.addEventListener('keydown', (e) => {
		if (e.key !== 'Escape') return;
		const stage = e.target.closest?.('[data-spot-stage].is-playing');
		if (stage) stop(stage);
	});
}
